import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Check, ExternalLink } from 'lucide-react';
import { AgendaItem } from '@/types';
import { sameDay } from '@/lib/dateUtils';
import { Badge, BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { TONE_DOT, TYPE_LABEL } from './EventPill';

interface Props {
  day: Date | null;
  events: AgendaItem[];
  onClose: () => void;
  onToggleDone?: (id: string) => void;
}

const TONE_BADGE: Record<AgendaItem['type'], BadgeTone> = {
  edital:   'primary',
  certidao: 'warning',
  contrato: 'success',
  sessao:   'info',
};

const TONE_STRIPE: Record<AgendaItem['type'], string> = {
  edital:   'bg-brand-500',
  certidao: 'bg-warning dark:bg-warning-dark',
  contrato: 'bg-success dark:bg-success-dark',
  sessao:   'bg-info dark:bg-info-dark',
};

function formatBRL(v?: string | number): string | null {
  if (v === undefined || v === null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.,-]/g, '').replace(',', '.'));
  if (!isFinite(n)) return typeof v === 'string' ? v : null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(n);
}

function navigateFor(e: AgendaItem): string {
  switch (e.type) {
    case 'edital':   return e.relatedId ? `/tenders` : '/tenders';
    case 'contrato': return '/contracts';
    case 'certidao': return '/atestados';
    case 'sessao':   return '/agenda';
  }
}

export function DayDrawer({ day, events, onClose, onToggleDone }: Props) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!day) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [day, onClose]);

  if (!day) return null;

  const isToday = sameDay(day, new Date());
  const weekday = day.toLocaleDateString('pt-BR', { weekday: 'long' });
  const monthAbbr = day.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="h-full w-full sm:w-[440px] sm:max-w-full bg-surface dark:bg-surface-dark sm:border-l border-line dark:border-line-dark sm:shadow-lg-warm dark:sm:shadow-lg-dark flex flex-col animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-4 pb-4 border-b border-line dark:border-line-dark flex-shrink-0">
          <div
            className={[
              'flex flex-col items-center justify-center w-[52px] h-[52px] rounded-lg flex-shrink-0',
              isToday
                ? 'bg-brand-500 text-white'
                : 'bg-surface-alt dark:bg-surface-dark-alt text-ink-title dark:text-ink-title-dark',
            ].join(' ')}
          >
            <div className={['text-[9px] font-semibold uppercase tracking-wider', isToday ? 'text-white/80' : 'text-ink-subtitle dark:text-ink-subtitle-dark'].join(' ')}>
              {monthAbbr}
            </div>
            <div className="text-[20px] font-extralight tabular-nums -mt-0.5">
              {day.getDate()}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-ink-title dark:text-ink-title-dark capitalize">
              {weekday}
            </h3>
            <p className="text-[12px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">
              {events.length} compromisso{events.length === 1 ? '' : 's'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="w-7 h-7 flex items-center justify-center rounded-md text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt hover:text-ink-text dark:hover:text-ink-text-dark transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {events.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
              Nenhum compromisso neste dia.
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {events.map(e => {
                const hour = String(e.hour ?? 9).padStart(2, '0');
                const valor = formatBRL(e.value);
                return (
                  <div
                    key={e.id}
                    className="relative pl-3 pr-3 py-3 rounded-lg bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark"
                  >
                    <div className={['absolute left-0 top-3 bottom-3 w-[3px] rounded-r', TONE_STRIPE[e.type]].join(' ')} />
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <Badge tone={TONE_BADGE[e.type]} dot>{TYPE_LABEL[e.type]}</Badge>
                      <span className="text-[11px] tabular-nums text-ink-subtitle dark:text-ink-subtitle-dark">
                        {hour}h
                      </span>
                    </div>
                    <div className={[
                      'text-[13px] font-medium text-ink-title dark:text-ink-title-dark leading-snug',
                      e.done ? 'line-through opacity-60' : '',
                    ].join(' ')}>
                      {e.title}
                    </div>
                    {e.subtitle && (
                      <div className="text-[11.5px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">
                        {e.subtitle}
                      </div>
                    )}
                    {valor && (
                      <div className="text-[11.5px] tabular-nums text-ink-text dark:text-ink-text-dark mt-1 font-medium">
                        {valor}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2.5">
                      <button
                        type="button"
                        onClick={() => { navigate(navigateFor(e)); onClose(); }}
                        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200"
                      >
                        Ver detalhes <ExternalLink size={12} />
                      </button>
                      {onToggleDone && (
                        <button
                          type="button"
                          onClick={() => onToggleDone(e.id)}
                          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-ink-subtitle dark:text-ink-subtitle-dark hover:text-success dark:hover:text-success-dark"
                        >
                          <Check size={12} /> {e.done ? 'Desfazer' : 'Concluído'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-line dark:border-line-dark flex-shrink-0">
          <Button variant="secondary" icon={<Plus size={14} />} className="w-full justify-center">
            Adicionar compromisso
          </Button>
        </div>
      </div>
    </div>
  );
}
